import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import axios from "axios";
import { AlertTriangle, ArrowRight, Banknote, BadgeCheck, Clock, CreditCard, Gavel, Info, Landmark, LayoutDashboard, Package, Scale, ShieldCheck, ShoppingBag, Sprout, Store, Tractor, TrendingUp, Users } from "lucide-react";
import { useApp } from "../context/AppContext";
import { useAuth, authHeaders } from "../context/AuthContext";
import { Skel } from "../components/Skeleton";
import { SAR } from "../souq";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ROLE_ICON = { admin: ShieldCheck, farmer: Sprout, trader: Store, buyer: ShoppingBag };

function Metric({ icon: Icon, label, value, sub, to, testId }) {
  const body = (<><Icon size={16} className="an-card-ic" /><span className="an-card-val">{value}</span><span className="an-card-label">{label}</span>{sub && <small className="hub-metric-sub">{sub}</small>}</>);
  return to ? <Link to={to} className="an-card hub-metric" data-testid={testId}>{body}</Link> : <div className="an-card hub-metric" data-testid={testId}>{body}</div>;
}

function Block({ id, icon: Icon, title, to, linkLabel, children }) {
  return (
    <section className="hub-block glass-card" data-testid={`hub-block-${id}`}>
      <div className="hub-block-head">
        <h2 className="dash-panel-title"><Icon size={17} /> {title}</h2>
        <Link to={to} className="btn-lux-ghost po-act" data-testid={`hub-card-${id}`}>{linkLabel} <ArrowRight size={13} className="hub-arrow" /></Link>
      </div>
      <div className="an-cards hub-cards">{children}</div>
    </section>
  );
}

// Full account hub (Phase B): role-aware live metrics + alerts, one aggregated request.
export default function DashboardHub() {
  const { t, lang } = useApp();
  const { user, logout } = useAuth();
  const [ov, setOv] = useState(null);
  useEffect(() => {
    if (user) axios.get(`${API}/dashboard/overview`, { headers: authHeaders() }).then((r) => setOv(r.data)).catch(() => setOv({ roles: user.roles, alerts: [] }));
  }, [user]);
  if (user === null) return <main className="dash-page"><Skel h={120} /></main>;
  if (user === false) return <Navigate to="/auth" replace />;

  const since = user.created_at || ov?.member_since;
  const sinceLabel = since ? new Date(since).toLocaleDateString(lang === "ar" ? "ar-SA-u-ca-gregory-nu-latn" : "en-GB", { month: "short", year: "numeric" }) : "";
  const money = (n) => SAR(n, lang);
  const f = ov?.farmer, tr = ov?.trader, b = ov?.buyer, a = ov?.admin;

  return (
    <main className="dash-page hub-page" data-testid="dashboard-hub">
      <header className="dash-header hub-header">
        <div className="hub-profile">
          {user.picture ? <img src={user.picture} alt="" className="hub-avatar" /> : <span className="hub-avatar hub-initial" aria-hidden>{(user.name || "?").trim().charAt(0)}</span>}
          <div>
            <span className="dash-kicker"><LayoutDashboard size={14} /> {t("hub.kicker")}</span>
            <h1 className="dash-title">{t("hub.welcome")}, {user.name}</h1>
            <p className="dash-sub" dir="auto"><span dir="ltr">{user.email}</span>{sinceLabel && <> · {t("hub.since")} <span dir="ltr">{sinceLabel}</span></>}</p>
            <div className="hub-roles" data-testid="hub-roles">
              {user.roles.map((r) => { const I = ROLE_ICON[r]; return <span key={r} className={`chip hub-role hub-role-${r}`} data-testid={`hub-role-${r}`}><I size={12} /> {t(`role.${r}`)}</span>; })}
            </div>
          </div>
        </div>
        <button className="btn-lux-ghost" onClick={logout} data-testid="hub-logout">{t("dash.logout")}</button>
      </header>

      {ov && ov.alerts.length > 0 && (
        <ul className="hub-alerts" data-testid="hub-alerts">
          {ov.alerts.map((al) => (
            <li key={al.id} className={`hub-alert ${al.level}`} data-testid={`hub-alert-${al.id}`}>
              {al.level === "warn" ? <AlertTriangle size={15} /> : <Info size={15} />}
              <span>{t(`hub.alert.${al.id}`).replace("{n}", al.count ?? "")}</span>
              <Link to={al.id === "pay" && al.order_id ? `/souq/payment/callback?order_id=${al.order_id}` : al.to} className="hub-alert-link" data-testid={`hub-alert-link-${al.id}`}>
                {al.id === "pay" ? t("hub.payNow") : t("hub.open")} <ArrowRight size={12} className="hub-arrow" />
              </Link>
            </li>
          ))}
        </ul>
      )}

      {!ov && <div className="an-cards"><Skel h={90} /><Skel h={90} /><Skel h={90} /></div>}

      {f && (
        <Block id="farmer" icon={Sprout} title={t("hub.farmer")} to="/provenance/dashboard" linkLabel={t("hub.openWorkspace")}>
          <Metric icon={Tractor} label={t("farms.count")} value={f.farms} to="/provenance/dashboard?tab=farms" testId="hub-farmer-farms" />
          <Metric icon={Scale} label={t("hub.kgSeason").replace("{y}", ov.season)} value={`${f.kg_season.toLocaleString("en")} kg`} sub={`${f.yields} ${t("hub.yields")} · ${f.certs_valid} ${t("hub.certs")}`} to="/provenance/dashboard?tab=timeline" testId="hub-farmer-kg" />
          <Metric icon={Gavel} label={t("hub.activeListings")} value={f.active_listings} sub={f.awaiting_decision ? `${f.awaiting_decision} ${t("hub.awaitingDecision")}` : null} to="/provenance/dashboard?tab=souq" testId="hub-farmer-listings" />
          <Metric icon={Banknote} label={t("po.pending")} value={money(f.pending_payout)} sub={`${t("po.transferred")}: ${money(f.transferred_payout)}`} to="/provenance/dashboard?tab=payouts" testId="hub-farmer-pending" />
          <Metric icon={TrendingUp} label={t("hub.seasonNet").replace("{y}", ov.season)} value={money(f.season_net)} sub={`${t("an.gross")}: ${money(f.season_gross)} · ${f.season_orders} ${t("po.orders")}`} to="/provenance/dashboard?tab=analytics" testId="hub-farmer-season" />
        </Block>
      )}

      {tr && (
        <Block id="trader" icon={Store} title={t("hub.trader")} to="/trader/dashboard" linkLabel={t("hub.openWorkspace")}>
          <Metric icon={Gavel} label={t("hub.activeListings")} value={tr.active_listings} sub={tr.awaiting_decision ? `${tr.awaiting_decision} ${t("hub.awaitingDecision")}` : null} to="/trader/dashboard" testId="hub-trader-listings" />
          <Metric icon={Banknote} label={t("po.pending")} value={money(tr.pending_payout)} to="/trader/dashboard?tab=payouts" testId="hub-trader-pending" />
          <Metric icon={TrendingUp} label={t("hub.salesSeason").replace("{y}", ov.season)} value={money(tr.season_net)} sub={`${tr.season_orders} ${t("po.orders")}`} to="/trader/dashboard?tab=payouts" testId="hub-trader-season" />
        </Block>
      )}

      {b && (
        <Block id="buyer" icon={ShoppingBag} title={t("hub.buyer")} to="/souq/dashboard" linkLabel={t("hub.openWorkspace")}>
          <Metric icon={Gavel} label={t("hub.activeBids")} value={b.active_bids} sub={`${b.winning} ${t("buyer.status.winning")} · ${b.outbid} ${t("buyer.status.outbid")}`} to="/souq/dashboard" testId="hub-buyer-bids" />
          <Metric icon={CreditCard} label={t("hub.awaitingPayment")} value={b.awaiting_payment.length} sub={b.awaiting_payment[0] ? money(b.awaiting_payment[0].amount) : null}
            to={b.awaiting_payment[0] ? `/souq/payment/callback?order_id=${b.awaiting_payment[0].order_id}` : "/souq/dashboard"} testId="hub-buyer-awaiting" />
          <Metric icon={Package} label={t("hub.purchases")} value={b.purchases} sub={money(b.purchases_total)} to="/souq/dashboard" testId="hub-buyer-purchases" />
        </Block>
      )}

      {a && (
        <Block id="admin" icon={ShieldCheck} title={t("hub.admin")} to="/admin" linkLabel={t("hub.openAdmin")}>
          <Metric icon={Users} label={t("hub.users")} value={a.users} to="/admin/farmers" testId="hub-admin-users" />
          <Metric icon={Gavel} label={t("hub.activeListings")} value={a.active_listings} to="/admin/souq" testId="hub-admin-listings" />
          <Metric icon={Package} label={t("hub.ordersMonth")} value={a.orders_month} sub={`${a.paid_month} ${t("po.paidOrders")}`} to="/admin/analytics" testId="hub-admin-orders" />
          <Metric icon={BadgeCheck} label={t("ba.adminTitle")} value={a.pending_requests} to="/admin/buyer-requests" testId="hub-admin-requests" />
          <Metric icon={Landmark} label={t("po.pendingRows")} value={a.pending_payout_rows} to="/admin/payouts" testId="hub-admin-payouts" />
        </Block>
      )}

      <div className="hub-grid">
        <Link to="/souq" className="hub-card glass-card" data-testid="hub-card-souq"><Gavel size={22} className="hub-card-ic" /><strong>{t("hub.souq")}</strong><p>{t("hub.souq.desc")}</p><span className="hub-card-go"><ArrowRight size={14} /></span></Link>
        {user.is_farmer && <Link to="/provenance/dashboard?tab=new" className="hub-card glass-card" data-testid="hub-card-new-yield"><Clock size={22} className="hub-card-ic" /><strong>{t("wq.tab.new")}</strong><p>{t("hub.newYield.desc")}</p><span className="hub-card-go"><ArrowRight size={14} /></span></Link>}
        {!user.can_buy && user.is_farmer && <Link to="/provenance/dashboard?tab=souq" className="hub-card glass-card" data-testid="hub-card-buyer-access"><ShoppingBag size={22} className="hub-card-ic" /><strong>{t("ba.title")}</strong><p>{t("ba.desc")}</p><span className="hub-card-go"><ArrowRight size={14} /></span></Link>}
      </div>
    </main>
  );
}
